import React, { useState, useEffect, useCallback } from 'react';
import { Package, PackageCheck, History, Database } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { db } from '../db';
import { supabase } from '../lib/supabase';
import LogViewer from './LogViewer';
import BackupManager from './BackupManager';
import type { SKU } from '../types';

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState<'overview' | 'logs' | 'backup'>('overview');
  const [stats, setStats] = useState({
    totalSKUs: 0,
    totalExpectedQuantity: 0,
    completedSKUs: 0,
    pendingSKUs: 0,
    totalLogs: 0,
    completionRate: 0, // 추가: 완료율
  });
  const [isLoading, setIsLoading] = useState(true);

  const syncStats = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      let skus: SKU[] = [];

      if (navigator.onLine) {
        const { data, error } = await supabase
          .from('skus')
          .select('*')
          .or(`user_id.eq.${session.user.id},user_id.is.null`);

        if (error) throw new Error(`SKU 조회 실패: ${error.message}`);

        skus = data.map((sku) => ({
          id: sku.id,
          styleNo: sku.style_no,
          color: sku.color,
          size: sku.size,
          expectedQuantity: sku.expected_quantity,
          actualQuantity: sku.actual_quantity,
          status: sku.status,
          progress: sku.progress,
          lastScanned: sku.last_scanned ? new Date(sku.last_scanned) : null,
          lastModified: new Date(sku.last_modified),
          userId: sku.user_id,
        }));

        // 변경된 데이터만 로컬 DB에 반영
        await db.transaction('rw', db.skus, async () => {
          await Promise.all(
            skus.map(async (sku) => {
              const localSku = await db.skus.get(sku.id);
              if (!localSku || localSku.lastModified < sku.lastModified) {
                await db.skus.put(sku);
              }
            })
          );
        });
      } else {
        toast.warn('오프라인 상태: 로컬 데이터를 사용합니다.');
        skus = await db.skus.toArray();
      }

      // 통계 계산
      const totalLogs = await db.logs.count();
      const totalSKUs = skus.length;
      const totalExpectedQuantity = skus.reduce((sum, sku) => sum + (sku.expectedQuantity || 0), 0);
      const completedSKUs = skus.filter((sku) => sku.status === 'completed').length;
      const completionRate = totalSKUs > 0 ? (completedSKUs / totalSKUs) * 100 : 0;

      setStats({
        totalSKUs,
        totalExpectedQuantity,
        completedSKUs,
        pendingSKUs: totalSKUs - completedSKUs,
        totalLogs,
        completionRate,
      });
    } catch (error: any) {
      console.error('통계 동기화 실패:', error.message);
      toast.error(`통계 동기화 실패: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    syncStats();
    const interval = setInterval(syncStats, 30000); // 30초마다 동기화
    return () => clearInterval(interval);
  }, [syncStats]);

  const StatCard = ({
    title,
    value,
    icon: Icon,
    subValue,
  }: {
    title: string;
    value?: number;
    icon: typeof Package;
    subValue?: string;
  }) => (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-semibold mt-2">
            {value !== undefined ? value.toLocaleString() : '-'}
          </p>
          {subValue && <p className="text-sm text-gray-500 mt-1">{subValue}</p>}
        </div>
        <div className="bg-blue-50 p-3 rounded-full">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex flex-wrap gap-4">
          <div className="h-10 w-24 bg-gray-200 rounded-lg animate-pulse" />
          <div className="h-10 w-24 bg-gray-200 rounded-lg animate-pulse" />
          <div className="h-10 w-24 bg-gray-200 rounded-lg animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-lg shadow-sm p-6">
              <div className="h-20 animate-pulse bg-gray-100" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-4">
        <button
          onClick={() => setActiveTab('overview')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'overview'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          통계
        </button>
        <button
          onClick={() => setActiveTab('logs')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'logs'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          작업 이력
        </button>
        <button
          onClick={() => setActiveTab('backup')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            activeTab === 'backup'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          데이터 관리
        </button>
      </div>

      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StatCard
            title="전체 SKU 수량"
            value={stats.totalSKUs}
            icon={Package}
          />
          <StatCard
            title="전체 입고 예정 수량"
            value={stats.totalExpectedQuantity}
            icon={PackageCheck}
          />
          <StatCard
            title="처리 완료 SKU"
            value={stats.completedSKUs}
            subValue={`완료율: ${stats.completionRate.toFixed(1)}%`}
            icon={PackageCheck}
          />
          <StatCard
            title="처리중 SKU"
            value={stats.pendingSKUs}
            icon={Package}
          />
          <StatCard
            title="작업 이력 수"
            value={stats.totalLogs}
            icon={History}
          />
        </div>
      )}

      {activeTab === 'logs' && <LogViewer />}
      {activeTab === 'backup' && <BackupManager />}
    </div>
  );
}